<template>
  <Form @submit="onSubmit">
    <div class="flex border-b border-dark-b">
      <h2 class="text-xl md:text-2xl font-semibold w-9/12 md:w-1/2">Work Experience</h2>
      <div class="flex lg:pl-2 text-xs items-center w-1/2 justify-end md:justify-between">
        <div>Progress</div>
        <div class="flex md:w-10/12 items-center justify-between">
          <div class="hidden md:inline w-1/5 h-2 ml-2 bg-green rounded-full"/>
          <div class="hidden md:inline w-4/5 h-2 bg-white rounded-r-full"/>
          <div class="pl-2 text-green md:text-black">20%</div>
        </div>
      </div>
    </div>
    <div class="flex flex-wrap w-full pt-12">
      <div class="font-semibold leading-5 text-lg lg:text-xl w-full">
        <div class="pb-1 flex items-end sm:items-center">
          <div>
            Background/
            <span class="hidden sm:inline">
              Professional Summary
            </span>
            <div class="sm:hidden">
              Professional Summary
            </div>
          </div>
          <span class="text-error">*</span>
          <span class="px-2 relative" 
            @mouseenter="showSummaryDetails = true"
            @mouseleave="showSummaryDetails = false"
          >
            ⓘ
            <div
              v-if="showSummaryDetails"
              class="absolute z-20 lg2:-top-24 lg2:left-5 right-0 pt-2 px-3"
            >
              <div class="px-2 py-2 w-60 sm:w-80 text-xs font-normal border rounded-lg bg-gray-b">
                To get noticed and get more orders write a concise description about your professional background. e.g. Originally from the UK, I have lived and worked in Hong Kong for nearly 15 years. I have worked for global brands like Timex, Guess, Kenneth Cole, Orange, Motorola, and have extensive experience working with smaller retail brands and startups focused primarily on technology accessories, wearables, and watches. I've worked with factories of all sizes all over China, bringing hundreds of products to market over my career.
              </div>
            </div>
          </span>
          <span class="hidden lg:inline text-sm font-normal">(Max 1000 characters)</span>
        </div>
        <div class="lg:hidden pb-2 text-sm font-normal">
          (Max 1000 characters)
        </div>
        <Field
          name="background"
          type="textarea"
          class="text-sm md:text-lg"
          :rules="isRequired"
          v-model="formData.background"
          v-slot="{field}"
        >
          <textarea 
            v-bind="field"
            class="border border-dark-b px-3 py-3 w-full rounded-lg bg-white h-52 max-h-52 lg:h-40 lg:max-h-40" 
            placeholder="Type text here..." 
          />
        </Field>
        <div class="h-7">
          <ErrorMessage name="background" class="form-error"/>
        </div>
      </div>
    </div>
    <div class="flex flex-wrap w-full items-end">
      <div class="lg:pr-5 w-full leading-5 text-lg lg:text-xl font-semibold lg:w-1/2">
        <div class="pb-1 flex items-center">
          <span>
            Total Years of Sourcing Experience
            <span class="text-error">*</span>
          </span>
        </div>
        <Field
          name="totalExperience"
          as="select"
          class="form-input"
          v-model="formData.total_experience"
        >
          <option value disabled>Year(s)</option>
          <option
            :key="`totalExperience-0`"
            :value="0.5"
          ><span>Less than </span>1 Year</option>
          <option
            v-for="(val) in 30"
            :key="`totalExperience-${val}`"
            :value="val"
          >{{ val }} Year<span v-if="val > 1">s</span></option>
          <option
            :key="`totalExperience-31`"
            :value="31"
          ><span>More than </span>30 Years</option>
        </Field>
        <div :class="[!assignmentLocations.length ? 'h-7' : 'h-3']">
          <span
            v-if="!formData.total_experience && totalExperienceError"
            class="form-error"
          >
            This field is required.
          </span>
        </div>
      </div>
      <div class="lg:pl-5 font-semibold text-lg lg:text-xl w-full lg:w-1/2">
        <div class="pb-1 flex items-center">
          <span>
            Location of Assignment
          <span class="px-1 text-sm relative" 
              @mouseenter="showLocationDetails = true"
              @mouseleave="showLocationDetails = false"
            >
              ⓘ
              <div
                v-if="showLocationDetails"
                class="absolute z-20 sm:-top-12 sm:left-5 pt-2 px-3"
              >
                <div class="px-2 py-2 w-44 text-xs font-normal border rounded-lg bg-gray-b">
                  Write the names of the Countries you have previously worked in. e.g. USA
                </div>
              </div>
          </span>
          <span class="text-sm font-normal">(Max 5)</span>
          </span>
        </div>
        <div class="flex flex-nowrap items-center justify-between">
          <div class="flex-grow country-field">
            <CountrySelect
              name="country"
              class="border border-dark-b h-12 px-3 rounded-lg bg-white w-full focus:text-opacity-100 tracking-tighter truncate sm:tracking-normal"
              placeholder="Choose your Country/Region"
              ref="countrySelect"
              :country="assignmentLocation"
              :class="{'text-dark text-opacity-50': assignmentLocation}"
              v-model="assignmentLocation"
            />
          </div>
          <button
            class="w-12 h-12 ml-5 rounded-lg bg-dark-blue text-white text-xl font-medium"
            type="button"
            :disable="assignmentLocations.length > 4"
            :class="{'opacity-50': assignmentLocations.length > 4}"
            @click="addAssignmentLocation"
          >+</button>
        </div>
        <div :class="[!assignmentLocations.length ? 'h-7' : 'h-3']">
          <span
            v-if="assignmentLocationError"
            class="form-error"
          >This field is required.</span>
        </div>
        <div class="lg:hidden flex flex-wrap" :class="{'pb-7' : assignmentLocations.length}">
          <div
            v-for="(loc, index) in assignmentLocations"
            :key="`assignment-location-${index}`"
            class="text-xs pr-2 pb-2"
          >
            <div class="bg-gray-b px-2 py-1 rounded-lg text-opacity-50 font-normal text-xs">
              <span class="pr-2">
                {{loc}}
              </span>
              <span
                class="cursor-pointer text-lg"
                @click="removeAssignmentLocation(index)"
              >x</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="hidden lg:flex flex-wrap w-full justify-end">
      <div class="w-1/2 pl-5">
        <div class="flex flex-wrap" :class="{'pb-7' : assignmentLocations.length}">
          <div
            v-for="(loc, index) in assignmentLocations"
            :key="`assignment-location-${index}`"
            class="text-xs pr-2 pb-2"
          >
            <div class="bg-gray-b px-2 py-1 rounded-lg text-opacity-50 font-normal text-xs">
              <span class="pr-2">
                {{loc}}
              </span>
              <span
                class="cursor-pointer text-lg"
                @click="removeAssignmentLocation(index)"
              >x</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-wrap w-full items-end">
      <div class="lg:pr-5 w-full leading-5 text-lg lg:text-xl font-semibold lg:w-1/2">
        <div class="pb-1 flex items-end sm:items-center">
          <div class="w-9/12 sm:w-full">
            Brand Names you have worked for
            <span class="px-1 text-sm relative" 
              @mouseenter="showBrandDetails = true"
              @mouseleave="showBrandDetails = false"
            >
              ⓘ
              <div
                v-if="showBrandDetails"
                class="absolute z-20 sm:-top-8 sm:left-5 pt-2 px-3"
              >
                <div class="px-2 py-2 w-44 text-xs font-normal border rounded-lg bg-gray-b">
                  Write the names of the Brands you have previously worked for. e.g. T-Glory
                </div>
              </div>
            </span>
            <span class="text-sm font-normal">(Max 5)</span>
          </div>
        </div>
        <div class="flex flex-nowrap items-center justify-between">
          <Field
            name="brandName"
            placeholder="Type text here...."
            class="border border-dark-b h-12 px-3 rounded-lg bg-white flex-grow text-sm md:text-lg focus:text-opacity-100"
            v-model="brandName"
          />
          <button
            class="w-12 h-12 ml-5 rounded-lg bg-dark-blue text-white text-xl font-medium"
            type="button"
            :disable="brandNames.length > 4"
            :class="{'opacity-50': brandNames.length > 4}"
            @click="addBrandName"
          >+</button>
        </div>
        <div :class="[(!brandNames.length || companies.length) ? 'h-7' : 'h-3']">
          <span
            v-if="brandNameError"
            class="form-error"
          >This field is required.</span>
        </div>
        <div class="lg:hidden flex flex-wrap" :class="{'pb-7' : brandNames.length}">
          <div
            v-for="(brand, index) in brandNames"
            :key="`brand-${index}`"
            class="text-xs pr-2 pb-2"
          >
            <div class="bg-gray-b px-2 py-1 rounded-lg text-opacity-50 font-normal text-xs">
              <span class="pr-2">
                {{brand}}
              </span>
              <span
                class="cursor-pointer text-lg"
                @click="removeBrandName(index)"
              >x</span>
            </div>
          </div>
        </div>
      </div>
      <div class="lg:pl-5 w-full leading-5 text-lg lg:text-xl font-semibold lg:w-1/2">
        <div class="pb-1 flex items-center tracking-tighter">
          <span>Name of Companies you have worked for
            <span class="px-1 text-sm relative" 
              @mouseenter="showCompanyDetails = true"
              @mouseleave="showCompanyDetails = false"
            >
              ⓘ
              <div
                v-if="showCompanyDetails"
                class="absolute z-20 sm:-top-10 sm:left-5 pt-2 px-3"
              >
                <div class="px-2 py-2 w-44 text-xs font-normal border rounded-lg bg-gray-b tracking-normal">
                  Write the names of the Companies you have previously worked for. e.g. Motorolla
                </div>
              </div>
          </span>
          <span class="text-sm font-normal">(Max 5)</span></span>
        </div>
        <div class="flex flex-nowrap items-center justify-between">
          <Field
            name="company"
            placeholder="Type text here...."
            class="border border-dark-b h-12 px-3 rounded-lg bg-white flex-grow text-sm md:text-lg focus:text-opacity-100"
            v-model="company"
          />
          <button
            class="w-12 h-12 ml-5 rounded-lg bg-dark-blue text-white text-xl font-medium"
            type="button"
            :disable="companies.length > 4"
            :class="{'opacity-50': companies.length > 4}"
            @click="addCompany"
          >+</button>
        </div>
        <div :class="[(!brandNames.length || companies.length) ? 'h-7' : 'h-3']">
          <span
            v-if="companyError"
            class="form-error"
          >This field is required.</span>
        </div>
        <div class="lg:hidden flex flex-wrap" :class="{'pb-7' : companies.length}">
          <div
            v-for="(company, index) in companies"
            :key="`company-${index}`"
            class="text-xs pr-2 pb-2"
          >
            <div class="bg-gray-b px-2 py-1 rounded-lg text-opacity-50 font-normal text-xs">
              <span class="pr-2">
                {{company}}
              </span>
              <span
                class="cursor-pointer text-lg"
                @click="removeCompany(index)"
              >x</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="hidden lg:flex flex-wrap w-full">
      <div class="w-1/2 pr-5">
        <div class="flex flex-wrap" :class="{'pb-7' : brandNames.length}">
          <div
            v-for="(brand, index) in brandNames"
            :key="`brand-${index}`"
            class="text-xs pr-2 pb-2"
          >
            <div class="bg-gray-b px-2 py-1 rounded-lg text-opacity-50 font-normal text-xs">
              <span class="pr-2">
                {{brand}}
              </span>
              <span
                class="cursor-pointer text-lg"
                @click="removeBrandName(index)"
              >x</span>
            </div>
          </div>
        </div>
      </div>
      <div class="w-1/2 pl-5">
        <div class="flex flex-wrap" :class="{'pb-7' : companies.length}">
          <div
            v-for="(company, index) in companies"
            :key="`company-${index}`"
            class="text-xs pr-2 pb-2"
          >
            <div class="bg-gray-b px-2 py-1 rounded-lg text-opacity-50 font-normal text-xs">
              <span class="pr-2">
                {{company}}
              </span>
              <span
                class="cursor-pointer text-lg"
                @click="removeCompany(index)"
              >x</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="flex justify-end pt-2 pb-3">
      <button
        class="bg-blue text-white py-3 px-8 text-lg lg:text-xl rounded-full font-bold"
        @click="validateFields"
      >
        Save
      </button>
    </div>
  </Form>
</template>
<script>
import { mapActions } from 'vuex'
import VeeValidate from '@/shared/mixins/VeeValidate'

export default {
  name: 'WorkExperience',
  mixins: [VeeValidate],
  data() {
    return {
      formData: {},
      totalExperienceError: false,
      assignmentLocations: [],
      assignmentLocation: '',
      assignmentLocationError: false,
      brandNames: [],
      brandName: '',
      brandNameError: false,
      companies: [],
      company: '',
      companyError: false,
      showSummaryDetails: false,
      showBrandDetails: false,
      showLocationDetails: false,
      showCompanyDetails: false
    }
  },
  computed: {
    isValidAssignmentLocation() {
      return !this.isDuplicateAssignmentLocation
    },
    isDuplicateAssignmentLocation() {
      return this.assignmentLocations.some(loc => (loc === this.assignmentLocation))
    },
    isValidBrandName() {
      return !this.isDuplicateBrandName
    },
    isDuplicateBrandName() {
      return this.brandNames.some(brand => (brand === this.brandName))
    },
    isValidCompanyName() {
      return !this.isDuplicateCompanyName
    },
    isDuplicateCompanyName() {
      return this.companies.some(company => (company === this.company))
    },
    isCustomError() {
      return this.assignmentLocationError || this.brandNameError || this.companyError || this.totalExperienceError
    }
  },
  methods: {
    ...mapActions([
      'updateWorkExperience'
    ]),
    addAssignmentLocation() {
      if (this.assignmentLocation && this.assignmentLocations.length < 5 && this.isValidAssignmentLocation) {
        this.assignmentLocations.push(this.assignmentLocation)
        this.assignmentLocation = ''
        this.assignmentLocationError = false
      }
    },
    removeAssignmentLocation(index) {
      this.assignmentLocations.splice(index, 1)
    },
    addBrandName() {
      if (this.brandName.length && this.brandNames.length < 5 && this.isValidBrandName) {
        this.brandNames.push(this.brandName)
        this.brandName = ''
        this.brandNamesError = false
      }
    },
    removeBrandName(index) {
      this.brandNames.splice(index, 1)
    },
    addCompany() {
      if (this.company.length && this.companies.length < 5 && this.isValidCompanyName) {
        this.companies.push(this.company)
        this.company = ''
        this.companyError = false
      }
    },
    removeCompany(index) {
      this.companies.splice(index, 1)
    },
    validateFields() {
      this.assignmentLocationError = !this.assignmentLocations.length
      this.totalExperienceError = !this.formData.total_experience
      this.brandNameError = !this.brandNames.length
      this.companyError = !this.companies.length
    },
    async onSubmit() {
      if (this.isCustomError) return
      const response = await this.updateWorkExperience(this.formatFormData())
      this.$notify({
        group: 'buyHive',
        type: response.result ? 'success' : 'error',
        text: response.message
      })
    },
    formatFormData() {
      const userData = new URLSearchParams()
      Object.keys(this.formData).forEach(key => {
        userData.append(key, this.formData[key] || '')
      })

      this.assignmentLocations.forEach((loc, index) => {
        userData.append(`locations_of_assignment[${index}]`, loc)
      })
      this.brandNames.forEach((brand, index) => {
        userData.append(`sourced_brands[${index}]`, brand)
      })
      this.companies.forEach((company, index) => {
        userData.append(`sourced_companies[${index}]`, company)
      })
      return userData
    }
  }
}
</script>
<style lang="scss" scoped>
  .country-field {
    max-width: calc(100% - 4.25rem);
  }
</style>